// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import { createPortal } from 'react-dom';
import ProgressBar from './ProgressBar';
import DualProgressBar from './DualProgressBar';
import * as NavUtils from '../utils/game/NavigationUtils';
import './WarpEffect.css';

const WarpEffect = ({
  show,
  warpDisengaging,
  hasArrived,
  selectedSystem,
  warpTravelTime,
  warpTravelProgress,
  warpElements,
  planetStars,
  antimatterFuel,
  fuelConsumptionRate,
  maxAntimatterFuel,
  fuelPercentage,
  onDisengage,
  onSkip,
  currentWarpFactor,
  onWarpFactorChange,
  distanceRemaining,
  fuelEfficiencyBonus
}) => {
  if (!show) return null;

  // Calculate estimated fuel remaining at current warp speed
  const calculateEstimatedFuel = (warpFactor) => {
    if (!distanceRemaining) return antimatterFuel;
    
    // Use NavUtils.calculateFuelNeeded for consistency with game calculations
    const newFuelNeeded = NavUtils.calculateFuelNeeded(
      distanceRemaining, 
      warpFactor, 
      fuelEfficiencyBonus || 0
    );
    
    // Estimate fuel at arrival
    const estimatedFuel = antimatterFuel - newFuelNeeded;
    return Math.max(0, estimatedFuel);
  };

  // Get color for warp factor button
  const getWarpColor = (factor) => {
    if (factor <= 3) return '#00ff88'; // Green - safe
    if (factor <= 6) return '#00ffff'; // Cyan - normal
    if (factor <= 7) return '#ffaa00'; // Orange - caution
    return '#ff4444'; // Red - dangerous
  };

  return createPortal(
    <div className={`warp-effect-container ${warpDisengaging ? 'disengaging' : ''}`}>
      {/* Warp tunnel with star streaks */}
      {!hasArrived && (
        <div className="warp-tunnel">
          {/* Vortex tunnel rings - cyan/blue */}
          <div className="warp-vortex-tunnel">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className="warp-vortex-ring"
                style={{
                  animation: `warpVortexExpand 3s linear infinite ${i * 0.3}s`
                }}
              />
            ))}
          </div>
          
          {/* Energy particles spiraling */}
          <div className="warp-energy-particles">
            {Array.from({ length: 40 }, (_, i) => {
              const angle = (i / 40) * 360;
              const distance = 20 + (i % 4) * 10;
              return (
                <div
                  key={i}
                  className="warp-particle"
                  style={{
                    '--angle': `${angle}deg`,
                    '--distance': `${distance}px`,
                    animation: `warpParticleSpiral 2s linear infinite ${i * 0.05}s`
                  }}
                />
              );
            })}
          </div>
          
          {/* Star streaks - flying past effect */}
          {warpElements.map((element) => (
            <div
              key={element.id}
              className="warp-star-streak"
              style={{
                '--start-x': `${element.startX}%`,
                '--start-y': `${element.startY}%`,
                '--end-x': `${element.moveXEnd}%`,
                '--end-y': `${element.moveYEnd}%`,
                '--duration': `${element.animationDuration}s`,
                '--delay': `${element.delay}s`,
                '--size': `${element.size}px`,
                '--color': element.color,
                '--angle': `${element.angle}deg`
              }}
            />
          ))}
          
          {/* Central warp core glow */}
          <div className="warp-core">
            <div className="warp-core-inner"></div>
            <div className="warp-core-pulse"></div>
          </div>
          
          {/* Radial speed lines */}
          <div className="speed-lines">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="speed-line"
                style={{
                  '--angle': `${(360 / 30) * i}deg`,
                  '--delay': `${i * 0.05}s`
                }}
              />
            ))}
          </div>
          
          {/* Disengage button */}
          <button className="disengage-warp-btn" onClick={onDisengage}>
            DISENGAGE WARP
          </button>
          
          {/* Dynamic Warp Speed Control - Right Side - Always show during warp */}
          <div className="warp-speed-control">
            <div className="warp-speed-label">WARP SPEED</div>
            <div className="warp-speed-bar">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(factor => (
                <button
                  key={factor}
                  className={`warp-speed-btn ${(currentWarpFactor || 1) === factor ? 'active' : ''}`}
                  style={{
                    borderColor: getWarpColor(factor),
                    color: (currentWarpFactor || 1) === factor ? '#000' : getWarpColor(factor),
                    background: (currentWarpFactor || 1) === factor 
                      ? `linear-gradient(135deg, ${getWarpColor(factor)}, ${getWarpColor(factor)}dd)`
                      : 'rgba(0, 20, 40, 0.8)',
                    boxShadow: (currentWarpFactor || 1) === factor 
                      ? `0 0 20px ${getWarpColor(factor)}`
                      : `0 0 5px ${getWarpColor(factor)}40`
                  }}
                  onClick={() => {
                    onWarpFactorChange && onWarpFactorChange(factor);
                  }}
                >
                  {factor}
                </button>
              ))}
            </div>
            <div className="warp-speed-estimate">
              <div className="estimate-label">EST. FUEL AT ARRIVAL</div>
              <div 
                className="estimate-value"
                style={{
                  color: calculateEstimatedFuel(currentWarpFactor || 1) > 50 ? '#00ff88' :
                         calculateEstimatedFuel(currentWarpFactor || 1) > 25 ? '#ffaa00' : '#ff4444'
                }}
              >
                {Math.round(calculateEstimatedFuel(currentWarpFactor || 1))}%
              </div>
            </div>
          </div>
          
          {/* Fuel Gauge */}
          <div className="warp-fuel-container">
            <div className="warp-fuel-label">
              ANTIMATTER FUEL
            </div>
            <ProgressBar 
              value={antimatterFuel || 0}
              max={maxAntimatterFuel || 100}
              color="auto"
              showLabel={true}
              animated={true}
              size="medium"
            />
            {fuelConsumptionRate && (
              <div className="warp-fuel-rate">
                -{fuelConsumptionRate.toFixed(2)}%/s
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Arrival screen with planet */}
      {hasArrived && (
        <div className="arrival-screen">
          {/* Starfield Background */}
          <div className="starfield">
            {planetStars?.map((star) => (
              <div
                key={star.id}
                className="planet-star"
                style={{
                  left: `${star.x}%`,
                  top: `${star.y}%`,
                  width: `${star.size}px`,
                  height: `${star.size}px`,
                  opacity: star.opacity,
                  animation: `twinkle 2s ease-in-out infinite ${star.twinkleDelay}s`
                }}
              />
            ))}
          </div>

          {/* Planet */}
          <div className="arrival-planet">
            <div className="planet-surface"></div>
            <div className="planet-atmosphere"></div>
            <div className="planet-shadow"></div>
          </div>
        </div>
      )}
      
      {/* Warp progress indicator */}
      {!hasArrived && (
        <div className="warp-progress-container">
          <div className="warp-progress-label">
            WARP TRAVEL: {Math.round(warpTravelProgress)}%
          </div>
          
          {/* Dual Progress Bar: Distance Traveled vs Fuel Remaining */}
          <DualProgressBar
            leftValue={warpTravelProgress}
            leftLabel="Distance Traveled"
            leftColor="#00ffff"
            rightValue={fuelPercentage}
            rightLabel="Fuel Remaining"
            rightColor="auto"
            animated={true}
            size="medium"
          />
          
          <div className="warp-progress-time">
            ETA: {Math.max(0, Math.ceil((warpTravelTime * (100 - warpTravelProgress)) / 100))}s
            {onSkip && warpTravelProgress > 10 && localStorage.getItem('warpSkipEnabled') !== 'false' && (
              <button className="warp-skip-btn" onClick={onSkip}>⏩ SKIP</button>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default WarpEffect;
